import {PayloadAction} from '@reduxjs/toolkit'
import {DocumentsTypes, IActionDocumentsRequest} from './types'
import {all, apply, put, race, take, takeLatest} from 'redux-saga/effects'
import {DocumentsActions} from './index'
import {SnackbarActions, SnackBarMessageType} from '../snackbar'
import Messages from '../../assets/locales/pt/translation.json'
import documentsService from '../../services/document.service'

/**
 * <h5>Generator functions that make calls to the authentication service to obtain the data.</h5>
 * @see {@link https://developer.mozilla.org/pt-BR/docs/Web/JavaScript/Reference/Statements/function*}
 * @typedef DocumentsSagas
 * @namespace DocumentsSagas
 * @category React
 * @subcategory Redux / Sagas
 */

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof DocumentsSagas
 * @alias DocumentsSagas.request
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionDocumentsRequest>} action User triggered action data.
 */
export function* request(action: PayloadAction<IActionDocumentsRequest>) {
    const { documentsSuccess, documentsFailure } = DocumentsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { type } = action.payload

        let serviceCall: any
        if (type === 'NFe') serviceCall = apply(documentsService, documentsService.nfeRequest, [action.payload])
        else if (type === 'CTe') serviceCall = apply(documentsService, documentsService.cteRequest, [action.payload])
        else if (type === 'MDFe') serviceCall = apply(documentsService, documentsService.mdfeRequest, [action.payload])
        else return

        const { result, cancelled } = yield race({
            result: serviceCall,
            cancelled: take(DocumentsTypes.DOCUMENTS_CANCEL)
        })

        if (cancelled) return

        yield put(documentsSuccess({ data: result, type }))
    } catch (err: any) {
        yield put(documentsFailure())
        if (err.response.status >= 500 && err.response.status <= 599) {
            const description: string = err.response?.data?.description || ''
            const { type } = action.payload

            if (type === 'NFe') {
                if (description === 'It is not possible to search for NFes.') {
                    yield put(openSnackBar({
                        title: 'Operação não realizada.',
                        type: SnackBarMessageType.WARNING,
                        message: `${Messages.REQUEST.DOCUMENTS.NFE.NOT_SEARCHABLE}`
                    }))
                } else if (description === 'The issuer and recipient provided in the filters are not part of the available databases.') {
                    yield put(openSnackBar({
                        title: 'Filtros inválidos.',
                        type: SnackBarMessageType.WARNING,
                        message: `${Messages.REQUEST.DOCUMENTS.NFE.INVALID_FILTERS}`
                    }))
                } else {
                    yield put(openSnackBar({
                        title: 'Erro Interno.',
                        type: SnackBarMessageType.ERROR,
                        message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
                    }))
                }
            } else if (type === 'CTe') {
                if (description === 'It is not possible to search for CTes.') {
                    yield put(openSnackBar({
                        title: 'Operação não realizada.',
                        type: SnackBarMessageType.WARNING,
                        message: `${Messages.REQUEST.DOCUMENTS.CTE.NOT_SEARCHABLE}`
                    }))
                } else if (description === 'The issuer and recipient provided in the filters are not part of the available databases.') {
                    yield put(openSnackBar({
                        title: 'Filtros inválidos.',
                        type: SnackBarMessageType.WARNING,
                        message: `${Messages.REQUEST.DOCUMENTS.CTE.INVALID_FILTERS}`
                    }))
                } else {
                    yield put(openSnackBar({
                        title: 'Erro Interno.',
                        type: SnackBarMessageType.ERROR,
                        message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
                    }))
                }
            } else if (type === 'MDFe') {
                if (description === 'It is not possible to search for MDFes.') {
                    yield put(openSnackBar({
                        title: 'Operação não realizada.',
                        type: SnackBarMessageType.WARNING,
                        message: `${Messages.REQUEST.DOCUMENTS.MDFE.CANNOT_SEARCH}`
                    }))
                } else if (description === 'The issuer provided in the filters is not part of the available databases.') {
                    yield put(openSnackBar({
                        title: 'Filtros inválidos.',
                        type: SnackBarMessageType.WARNING,
                        message: `${Messages.REQUEST.DOCUMENTS.MDFE.CANNOT_SEARCH_DESC}`
                    }))
                } else {
                    yield put(openSnackBar({
                        title: 'Erro Interno.',
                        type: SnackBarMessageType.ERROR,
                        message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
                    }))
                }
            } else {
                yield put(openSnackBar({
                    title: 'Erro Interno.',
                    type: SnackBarMessageType.ERROR,
                    message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
                }))
            }
        }
    }

}

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof CompanySagas
 * @alias CompanySagas.itens
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionDocumentsRequest>} action User triggered action data.
 */
export function* itens(action: PayloadAction<IActionDocumentsRequest>) {
    const { itensSuccess, itensFailure } = DocumentsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { access_key, type } = action.payload
        if (type === 'NFe') {
            // @ts-ignore
            const result = yield apply(documentsService, documentsService.itensNfeRequest, [access_key])
            yield put(itensSuccess({
                data: {
                    notas: result
                }
            }))
        }
    } catch (err: any) {
        yield put(itensFailure())
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* danfe(action: PayloadAction<IActionDocumentsRequest>) {
    const { danfeSuccess, danfeFailure } = DocumentsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { access_key } = action.payload
        // @ts-ignore
        const result = yield apply(documentsService, documentsService.danfe, [access_key])
        const url = URL.createObjectURL(result)
        window.open(url, '_blank')
        yield put(danfeSuccess())
    } catch (err: any) {
        yield put(danfeFailure())
        if (err.response.status >= 400 && err.response.status <= 499) {
            if (err.response.data.description === 'Nfe not found or already removed. A new operation for the same resource is required.') {
                yield put(openSnackBar({
                    title: 'Operação não realizada',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.DOCUMENTS.DANFE.NOT_FOUND}`
                }))
            }
        }
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

const documentsSaga = function* () {
    yield all([
        takeLatest(DocumentsTypes.DOCUMENTS_REQUEST, request),
        takeLatest(DocumentsTypes.ITENS_REQUEST, itens),
        takeLatest(DocumentsTypes.DANFE_REQUEST, danfe)
    ])
}

export default documentsSaga
